import type * as THREE from "three";
import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import type { QualityTier } from "@rpg/look";
import { checkBudget } from "./budget";
import { deviceBlock, glStrings, isSoftwareGL, textureMemory } from "./device";
import { perfReport, setPerfReport, type PerfOverlay } from "./overlay";
import { pick, r2, summarize } from "./stats";

// The scripted route tour (from the superposition spike's gate 3): the game supplies the route as segments (each a
// function of game time and the player's state, so every device walks the same route and sees the same views); the
// tour runs them, and every frame records the frame time (requestAnimationFrame deltas), the CPU time of the frame's
// callbacks, the GPU time when EXT_disjoint_timer_query_webgl2 exists (a query around the frame's renderer.render),
// and renderer.info's counts. At the end it builds the report (device, quality, timing percentiles, counts, texture
// memory, the §5 budget check, per-segment stats), shows it in the overlay and puts it in the report slot.
//
// Fast-forward (`ffMs` > 0): the tour steps the sim itself, `ffMs` of game time per drawn frame in 50 ms slices,
// steering between them (counts real, frame times meaningless: for software GL).

/** One leg of the route. */
export interface TourSegment {
  name: string;
  /** Called once at the segment's start. */
  start?: () => void;
  /** Called every frame (or every 50 ms of game time in fast-forward); returns true when the segment is done. */
  tick: (t: number) => boolean;
  /** Game-time timeout (s). */
  max: number;
}

/** What the tour drives: the game's clock and pause, and (fast-forward) its stepping. */
export interface TourGame {
  /** Game time (s). */
  simT(): number;
  pause(on: boolean): void;
  advanceTime(ms: number, draw?: boolean): void;
}

export interface TourOptions {
  /** The report's `kind`. */
  kind: string;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  composer: EffectComposer | null;
  game: TourGame;
  segments: TourSegment[];
  /** The name of the warm-up segment, left out of the overall numbers (default "warmup"). */
  warmup?: string;
  /** Fast-forward: game ms per drawn frame (0: real time). */
  ffMs: number;
  /** The route's label in the report ("short", "full"). */
  route: string;
  /** About how long the route is in game time (s), for the status line. */
  estSeconds: number;
  tier: QualityTier;
  ui: PerfOverlay;
  /** performance.now() when the page was ready (reported). */
  readyAt: number;
  /** The report's quality block. */
  quality: () => Record<string, unknown>;
  /** Extra report blocks (for example the world streamer's stats), added after `heap`. */
  extra?: () => Record<string, unknown>;
  /** Called after pausing (or unpausing) and before the first segment (reset the game's own stats). */
  onStart?: () => void;
  /** Called when the route is done, before the report (put the game's controls back). */
  onEnd?: () => void;
}

interface Sample {
  seg: number;
  simT: number;
  frameMs: number;
  cpuMs: number;
  gpuMs: number | null;
  calls: number;
  triangles: number;
}

/** Run the tour; the report lands in the slot (perfReport()) and the overlay. */
export function runTour(o: TourOptions): void {
  const { renderer, game, segments: segs } = o;
  const simT = (): number => game.simT();
  const warmName = o.warmup ?? "warmup";

  // ── measuring ──
  const samples: Sample[] = [];
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const timer = gl.getExtension("EXT_disjoint_timer_query_webgl2") as { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  let open: WebGLQuery | null = null;
  const pending: { q: WebGLQuery; i: number }[] = [];
  const baseRender = renderer.render.bind(renderer);
  if (timer)
    renderer.render = (scene, camera) => {
      if (!open && running) {
        open = gl.createQuery();
        if (open) gl.beginQuery(timer.TIME_ELAPSED_EXT, open);
      }
      baseRender(scene, camera);
    };
  const closeQuery = (i: number): void => {
    if (!timer || !open) return;
    gl.endQuery(timer.TIME_ELAPSED_EXT);
    pending.push({ q: open, i });
    open = null;
  };
  const pollQueries = (): void => {
    if (!timer) return;
    const disjoint = gl.getParameter(timer.GPU_DISJOINT_EXT) as boolean;
    while (pending.length && gl.getQueryParameter(pending[0]!.q, gl.QUERY_RESULT_AVAILABLE)) {
      const { q, i } = pending.shift()!;
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      if (!disjoint && samples[i]) samples[i]!.gpuMs = ns / 1e6;
      gl.deleteQuery(q);
    }
  };

  let seg = -1;
  const nextSeg = (): void => {
    seg++;
    const s = segs[seg];
    if (!s) return;
    segStart = simT();
    s.start?.();
  };
  let segStart = 0;
  let running = true;
  const t0 = performance.now();
  /** Segments that ran out of time (the player stuck on the route): reported, so a bad run is visible. */
  const timeouts = new Set<number>();
  const tick = (): boolean => {
    const s = segs[seg];
    if (!s) return true;
    const t = simT();
    const timedOut = t - segStart > s.max;
    if (timedOut) timeouts.add(seg);
    if (s.tick(t) || timedOut) {
      nextSeg();
      return seg >= segs.length;
    }
    return false;
  };

  const { ffMs } = o;
  game.pause(!!ffMs);
  o.onStart?.();
  nextSeg();
  let lastTs = -1;
  let lastUi = 0;
  const tourT0 = simT();

  const frame = (ts: number): void => {
    // CPU: this frame's earlier callbacks (the game's sim steps and draw submission), or in fast-forward the tour's own
    // stepping and drawing (many sim steps a frame, so only comparable between fast-forward runs)
    let cpuMs = performance.now() - ts;
    const i = samples.length;
    if (ffMs) {
      const c0 = performance.now();
      // fast-forward: step the route in 50 ms slices (steering between them), drawing the last
      const n = Math.max(1, Math.round(ffMs / 50));
      for (let j = 0; j < n && seg < segs.length; j++) {
        if (tick()) break;
        game.advanceTime(50, j === n - 1);
      }
      cpuMs = performance.now() - c0;
    }
    closeQuery(i);
    if (lastTs >= 0) {
      const info = renderer.info.render;
      samples.push({ seg: Math.max(0, Math.min(seg, segs.length - 1)), simT: simT(), frameMs: ts - lastTs, cpuMs, gpuMs: null, calls: info.calls, triangles: info.triangles });
    }
    lastTs = ts;
    pollQueries();
    const done = ffMs ? seg >= segs.length : tick();
    const segName = segs[Math.min(seg, segs.length - 1)]!.name;
    if (perfReport()?.segment !== segName) setPerfReport({ kind: o.kind, status: "running", segment: segName, frames: samples.length });
    if (ts - lastUi > 500) {
      lastUi = ts;
      o.ui.status(`perf tour · ${segName} · ${(simT() - tourT0).toFixed(1)} / ~${o.estSeconds} s game time · tier ${o.tier}${ffMs ? " · fast-forward" : ""}`);
    }
    if (!done) {
      requestAnimationFrame(frame);
      return;
    }
    running = false;
    o.onEnd?.();
    // let the last timer queries land, then report
    let waits = 0;
    const finish = (): void => {
      pollQueries();
      if (pending.length && waits++ < 30) {
        requestAnimationFrame(finish);
        return;
      }
      const report = buildReport(o, samples, segs.map((s) => s.name), warmName, { ffMs, wallMs: performance.now() - t0, gpuTimer: !!timer, timeouts });
      setPerfReport(report);
      o.ui.done(report);
      console.info("[perf] tour done", JSON.stringify(report));
    };
    finish();
  };
  setPerfReport({ kind: o.kind, status: "running" });
  requestAnimationFrame(frame);
}

function buildReport(o: TourOptions, all: Sample[], names: string[], warmName: string, run: { ffMs: number; wallMs: number; gpuTimer: boolean; timeouts: Set<number> }): Record<string, unknown> {
  const { renderer } = o;
  const softwareGL = isSoftwareGL(glStrings(renderer.getContext() as WebGL2RenderingContext).renderer);
  const warm = all.filter((s) => names[s.seg] === warmName);
  const body = all.filter((s) => names[s.seg] !== warmName);
  const frame = summarize(body.map((s) => s.frameMs));
  const gpuVals = body.map((s) => s.gpuMs).filter((v): v is number => v !== null);
  const gpu = gpuVals.length ? { ...summarize(gpuVals), samples: gpuVals.length } : null;
  const calls = body.map((s) => s.calls);
  const tris = body.map((s) => s.triangles);
  const worst = body.reduce<Sample | null>((w, s) => (!w || s.frameMs > w.frameMs ? s : w), null);
  const maxCalls = Math.max(0, ...calls);
  const maxTris = Math.max(0, ...tris);
  const tex = textureMemory(o);
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  const MB = 1024 * 1024;
  const postPasses = o.composer ? o.composer.passes.filter((p) => p.enabled && !(p instanceof RenderPass)).length : 0;
  let shadowMapSize = 0;
  o.scene.traverse((x) => {
    const l = x as THREE.DirectionalLight;
    if (l.isLight && l.castShadow) shadowMapSize = Math.max(shadowMapSize, l.shadow.mapSize.x);
  });
  const budget = checkBudget({
    tier: o.tier,
    drawCallsMax: maxCalls,
    trianglesMax: maxTris,
    textureMBEst: tex.totalMB,
    shadowMapSize,
    pixelRatio: renderer.getPixelRatio(),
    postPasses,
    frameMsP95: frame.p95,
    gpuMsP95: gpu?.p95 ?? null,
    softwareGL,
  });
  const segments = names.map((name, i) => {
    const ss = all.filter((s) => s.seg === i);
    const f = summarize(ss.map((s) => s.frameMs));
    return { name, frames: ss.length, timedOut: run.timeouts.has(i), frameMs: { p50: f.p50, p95: f.p95, worst: f.worst }, drawCallsMax: Math.max(0, ...ss.map((s) => s.calls)), trianglesMax: Math.max(0, ...ss.map((s) => s.triangles)) };
  });
  const info = renderer.info;
  return {
    kind: o.kind,
    version: 1,
    status: "done",
    when: new Date().toISOString(),
    url: location.href,
    mode: run.ffMs ? `fast-forward ${run.ffMs} ms/frame (counts only)` : "real time",
    route: o.route,
    device: deviceBlock(renderer, run.gpuTimer),
    quality: o.quality(),
    timing: {
      readySinceNavigationMs: Math.round(o.readyAt),
      tourWallSeconds: r2(run.wallMs / 1000),
      frames: body.length,
      fps: r2(body.length / Math.max(0.001, body.reduce((a, s) => a + s.frameMs, 0) / 1000)),
      frameMs: frame,
      cpuMs: summarize(body.map((s) => s.cpuMs)),
      gpuMs: gpu,
      hitches: { over33ms: body.filter((s) => s.frameMs > 33.4).length, over50ms: body.filter((s) => s.frameMs > 50).length, over100ms: body.filter((s) => s.frameMs > 100).length },
      warmup: { frames: warm.length, worstMs: r2(Math.max(0, ...warm.map((s) => s.frameMs))) },
      worstFrame: worst ? { ms: r2(worst.frameMs), segment: names[worst.seg], gameT: worst.simT, drawCalls: worst.calls, triangles: worst.triangles } : null,
    },
    counts: {
      drawCalls: { max: maxCalls, ...pick(summarize(calls), "p50", "p95", "mean") },
      triangles: { max: maxTris, ...pick(summarize(tris), "p50", "p95", "mean") },
      programs: info.programs?.length ?? 0,
      textures: info.memory.textures,
      geometries: info.memory.geometries,
      textureMemoryEst: tex,
      postPasses,
      shadowMapSize,
    },
    heap: mem ? { usedMB: r2(mem.usedJSHeapSize / MB), totalMB: r2(mem.totalJSHeapSize / MB), limitMB: r2(mem.jsHeapSizeLimit / MB) } : null,
    ...o.extra?.(),
    budget,
    segments,
  };
}
